import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getOrgSettings as fetchOrgSettings } from '../api';
import { useAuth } from './AuthContext';
import { PLATFORM_NAME } from '../branding';

// ────────────────────────────────────────────────────────────────────────────
// Default values — must mirror server/src/controllers/orgSettingsController.ts
// Used until the API call returns, and as fallback if the call fails.
// ────────────────────────────────────────────────────────────────────────────

export const ORG_DEFAULTS = {
  general: {
    // Blank on purpose: the real name comes from this firm's settings, and
    // useFirmName() falls back to the Tenant record. A hardcoded firm name here
    // would flash another firm's name during load.
    orgName: '',
    fiscalYearStartMonth: 4,
    currency: 'INR',
    workingDays: [1, 2, 3, 4, 5, 6],
    dateFormat: 'DD/MM/YYYY',
    timeZone: 'Asia/Kolkata',
  },
  attendance: {
    photoRequired: true,
    locationRequired: true,
    adminBypassProof: true,
    hrBypassProof: false,
    autoFillAbsent: true,
    blockBackdatedSelfMark: true,
    blockRemarkSameDay: true,
    purgeOldRecordsDays: 45,
  },
  leave: {
    accrualPerMonth: 1.5,
    compOffEnabled: true,
    leaveApprovalRequired: true,
    overlapCheckEnabled: true,
  },
  tasks: {
    taskIdPrefix: 'T',
    taskIdPadLength: 4,
    defaultStatus: 'OPEN',
    defaultBillingStatus: 'UNBILLED',
    requireArchiveOnClose: true,
    caCategoryName: 'CA Certification',
  },
  invoicing: {
    defaultTaxType: 'NONE',
    defaultCgstRate: 9,
    defaultSgstRate: 9,
    defaultIgstRate: 18,
    defaultDueDays: 30,
    showHsnSac: true,
    requireHsnSac: false,
    defaultTdsSection: '',
    defaultPaymentMode: 'BANK',
    creditNotePrefix: 'CN-',
    invoiceEditWindowDays: 7,
    draftInvoicesAlwaysEditable: true,
    retainerCloseBufferDays: 5,
    retainerMonthlyBillFromDay: 25,
    wizardOpeDefaultOn: true,
    defaultInvoiceTemplate: 1,
    wizardDefaultDescriptionTemplate: '{taskName} - {periodLabel}',
  },
  timesheets: {
    weekStartsOn: 1,
    lockEnabled: true,
    allowBackdatedEntry: true,
    blockOnApprovedLeave: true,
    blockOnClosedTask: true,
    hoursStep: 1,
  },
  security: {
    sessionTimeoutHours: 8,
    bcryptRounds: 10,
    minPasswordLength: 6,
    requireStrongPassword: false,
    otpLoginEnabled: true,
    passkeyLoginEnabled: true,
  },
  hr: {
    workingHoursPerDay: 8,
    weeklyOffDays: [0],
  },
  reimbursement: {
    requireReceiptAboveAmount: 0,
    maxClaimPerSubmission: 50000,
    autoApproveBelowAmount: 0,
    notifyOnSubmit: true,
    notifyOnDecision: true,
  },
  tickets: {
    defaultPriority: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT',
    defaultType: 'SOFTWARE' as 'SOFTWARE' | 'HARDWARE' | 'NETWORK' | 'OTHER',
    slaHoursUrgent: 4,
    slaHoursHigh: 24,
    slaHoursMedium: 72,
    slaHoursLow: 168,
    autoCloseResolvedAfterDays: 7,
    costApprovalRequiredAbove: 5000,
  },
  notifications: {
    taskDueReminderDays: 3,
    taskReminderCronTime: '08:00',
    invoiceOverdueReminderDays: 7,
    attendanceReminderEnabled: true,
    emailNotificationsEnabled: true,
    smsNotificationsEnabled: true,
    gstFilingReminderEnabled: true,
    reimbursementPaidNotification: true,
    monthlyDigestEnabled: true,
  },
  kpi: {
    healthScoreGoodMin: 75,
    healthScoreAtRiskMin: 50,
    billingRealizationTargetPct: 70,
    collectionRateTargetPct: 80,
    overdueInvoiceDaysThreshold: 30,
  },
  roles: {
    staffCanViewAllTasks: true,
    staffCanCreateTickets: true,
    staffCanCreateReimbursement: true,
    hrCanResetPasswords: true,
    partnerCanDeleteInvoices: false,
    staffCanViewOtherTimesheets: false,
    staffCanViewAllInvoices: false,
    staffCanCreateInvoice: true,
    hrCanAccessBilling: true,
    hrCanApproveReimbursements: false,
    hrCanMarkReimbursementsPaid: true,
    partnerCanDeleteClients: false,
    partnerCanRunGstCheck: true,
    partnerCanImportOpeningBalance: true,
    staffCanSeeOwnReimbursementPaid: true,
    itCanViewAllTickets: true,
    staffCanExportData: true,
    restrictOpeningBalanceToAdminHR: false,
  },
  storage: {
    maxFileSizeMB: 10,
    allowImages: true,
    allowPdf: true,
    allowOfficeDocs: true,
    allowZip: false,
    vaultRetentionDays: 0,
    chatRetentionDays: 0,
    notificationRetentionDays: 90,
    excelExportEnabled: true,
    maxExportRows: 10000,
  },
  gst: {
    gstProvider: 'taxpro' as 'taxpro' | 'whitebooks',
    whitebooksClientId: '',
    whitebooksClientSecretEnc: '',   // server returns '••••••••' when a secret is set
    whitebooksClientSecretSet: false, // read-only flag from server
    whitebooksEmail: '',
    gstAutoCheckEnabled: true,
    gstCheckMorningHour: 8,
    gstCheckMidHour: 14,
    gstCheckEveningHour: 20,
    gstApiRateLimitMs: 200,
    gstReturnTypes: ['GSTR-1', 'GSTR-3B', 'GSTR-6'],
  },
  receivables: {
    openingBalanceEnabled: true,
    autoMarkOverdue: true,
    overdueGracePeriodDays: 0,
    groupReceivablesByEntity: false,
    maxShortExcessRupees: 100,
  },
  receipts: {
    numberPrefix: 'RCPT',
    numberPadding: 4,
    tdsAutoSuggestPct: 10,
    roundingToleranceRupees: 1,
    paidThresholdRupees: 0,
    disclaimerText: 'System-generated receipt. Subject to realisation of the instrument.',
  },
  reconciliation: {
    amountMatchToleranceRupees: 1,
    tdsNetMatchToleranceRupees: 2,
    tdsRatesPct: [10, 2, 5, 7.5],
    bulkConfirmMinScore: 100,
  },
  documents: {
    pdfHeaderColor: '#0C2340',
    pdfAccentColor: '#8B1A1A',
    invoiceFooterText: 'This is a Computer Generated Invoice',
  },
  email: {
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    smtpUser: 'noreply@mgsg.in',
    smtpPassEncrypted: '',      // server returns '••••••••' when a password is set
    smtpPassSet: false,         // read-only flag from server
    fromName: '',
    fromAddress: 'noreply@mgsg.in',
    secure: false,
    provider: 'smtp' as 'smtp' | 'graph',
  },
  billing: {
    senderEmail: 'accounts@mgsg.in',
    senderName: '',
    defaultCc: 'accounts@mgsg.in',
    subjectTemplate: 'Invoice {invoiceNumber} from {firmName}',
    bodyTemplate:
      '<p>Dear Sir/Madam,</p>' +
      '<p>Please find attached invoice <strong>{invoiceNumber}</strong> dated <strong>{invoiceDate}</strong> for <strong>₹{amount}</strong> towards services rendered.</p>' +
      '<p>Kindly arrange for payment by the due date (<strong>{dueDate}</strong>).</p>' +
      '<p>Warm regards,<br/>{senderName}<br/>{firmName}</p>',
  },
};

export type OrgSettings = typeof ORG_DEFAULTS & {
  _meta?: {
    updatedAt: string | null;
    updatedBy: { id: number; staffName: string } | null;
  };
};

interface OrgSettingsContextType {
  settings: OrgSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  setSettings: (s: OrgSettings) => void; // for after a successful PUT
  // Helpers — return common derived values
  currencySymbol: () => string;
  formatCurrency: (n: number) => string;
}

const OrgSettingsContext = createContext<OrgSettingsContextType | null>(null);

// Module-level snapshot of the latest settings, kept in sync by the provider
// below. Lets plain (non-component) helpers — PDF generators, formatters —
// read current settings without needing every call site to thread them
// through as a parameter. Prefer useOrgSettings() inside components; this is
// for code that genuinely can't call a hook.
let latestSettings: OrgSettings = ORG_DEFAULTS as OrgSettings;
export function getLatestOrgSettings(): OrgSettings {
  return latestSettings;
}

function currencySymbolOf(currency: string): string {
  switch (currency) {
    case 'INR': return '₹';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    default: return currency + ' ';
  }
}

// Deep-merge a partial settings object with defaults so missing keys fall back
function mergeWithDefaults(raw: any): OrgSettings {
  const out: any = {};
  for (const cat of Object.keys(ORG_DEFAULTS) as (keyof typeof ORG_DEFAULTS)[]) {
    out[cat] = { ...ORG_DEFAULTS[cat], ...(raw?.[cat] ?? {}) };
  }
  if (raw?._meta) out._meta = raw._meta;
  return out as OrgSettings;
}

export const OrgSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [settings, setSettingsState] = useState<OrgSettings>(ORG_DEFAULTS as OrgSettings);
  const [loading, setLoading] = useState(false);

  const applySettings = (s: OrgSettings) => {
    latestSettings = s;
    setSettingsState(s);
  };

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetchOrgSettings();
      applySettings(mergeWithDefaults(res.data));
    } catch {
      // Keep defaults — quiet failure so the app stays usable
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Re-fetch when the user logs in (or token changes)
  useEffect(() => {
    if (user && token) refresh();
  }, [user, token, refresh]);

  const setSettings = useCallback((s: OrgSettings) => {
    applySettings(mergeWithDefaults(s));
  }, []);

  const currencySymbol = useCallback(() => currencySymbolOf(settings.general.currency), [settings.general.currency]);

  const formatCurrency = useCallback(
    (n: number) => {
      const sym = currencySymbolOf(settings.general.currency);
      const locale = settings.general.currency === 'INR' ? 'en-IN' : 'en-US';
      return `${sym}${Number(n).toLocaleString(locale, { maximumFractionDigits: 0 })}`;
    },
    [settings.general.currency]
  );

  return (
    <OrgSettingsContext.Provider value={{ settings, loading, refresh, setSettings, currencySymbol, formatCurrency }}>
      {children}
    </OrgSettingsContext.Provider>
  );
};

/**
 * The name of the firm the signed-in user works for.
 *
 * Order matters: the firm's own configured name wins, then the Tenant record
 * (always present, set at onboarding), and only then the product name — which
 * is a last resort for the brief window before either has loaded.
 */
export const useFirmName = (): string => {
  const { settings } = useOrgSettings();
  const { firmName } = useAuth();
  return settings.general.orgName?.trim() || firmName || PLATFORM_NAME;
};

export const useOrgSettings = () => {
  const ctx = useContext(OrgSettingsContext);
  if (!ctx) throw new Error('useOrgSettings must be used within OrgSettingsProvider');
  return ctx;
};
